// utils/pageUsageService.ts - Frontend service for page usage tracking
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export interface PageUsage {
  _id: string;
  pagePath: string;
  pageTitle: string;
  category: string;
  visitCount: number;
  lastVisited: string;
  firstVisited: string;
  avgTimeSpent: number;
  totalTimeSpent: number;
  isFavorite: boolean;
  reason?: 'favorite' | 'most_used' | 'recent';
}

export interface PageVisit {
  pagePath: string;
  pageTitle: string;
  timeSpent?: number;
  category?: string;
}

export interface AnalyticsData {
  byCategory: Array<{
    _id: string;
    totalVisits: number;
    totalTimeSpent: number;
    pageCount: number;
    avgVisitsPerPage: number;
  }>;
  overall: {
    totalPages: number;
    totalVisits: number;
    totalTimeSpent: number;
    favoriteCount: number;
  };
}

class PageUsageService {
  private static getAuthToken(): string | null {
    return localStorage.getItem('authToken');
  }

  private static getHeaders(): HeadersInit {
    const token = this.getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // Track a page visit
  private static _pendingTracks: Map<string, Promise<any>> = new Map();

  static async trackPageVisit(visit: PageVisit): Promise<PageUsage | null> {
    try {
      // Deduplicate concurrent track calls for the same pagePath/timeSpent=0
      const key = `${visit.pagePath}::${visit.timeSpent ?? 0}`;
      if (this._pendingTracks.has(key)) return this._pendingTracks.get(key);
      const p = (async () => {
        const response = await fetch(`${API_BASE_URL}/page-usage/track`, {
          method: 'POST',
          headers: this.getHeaders(),
          body: JSON.stringify(visit)
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        return result.success ? result.data : null;
      })();
      this._pendingTracks.set(key, p);
      try { const res = await p; return res; } finally { this._pendingTracks.delete(key); }
    } catch (error) {
      console.error('Error tracking page visit:', error);
      return null;
    }
  }

  // Get most used pages for quick access
  static async getMostUsedPages(limit: number = 5): Promise<PageUsage[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/page-usage/most-used?limit=${limit}`, {
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.success ? result.data : [];
    } catch (error) {
      console.error('Error getting most used pages:', error);
      return [];
    }
  }

  // Get recently visited pages
  static async getRecentlyVisited(limit: number = 5): Promise<PageUsage[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/page-usage/recent?limit=${limit}`, {
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.success ? result.data : [];
    } catch (error) {
      console.error('Error getting recent pages:', error);
      return [];
    }
  }

  // Get pages by category
  static async getPagesByCategory(category: string, limit: number = 10): Promise<PageUsage[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/page-usage/category/${category}?limit=${limit}`, {
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.success ? result.data : [];
    } catch (error) {
      console.error('Error getting pages by category:', error);
      return [];
    }
  }

  // Get favorite pages
  static async getFavoritePages(): Promise<PageUsage[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/page-usage/favorites`, {
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.success ? result.data : [];
    } catch (error) {
      console.error('Error getting favorite pages:', error);
      return [];
    }
  }

  // Toggle favorite status
  static async toggleFavorite(pagePath: string): Promise<PageUsage | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/page-usage/favorite/toggle`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ pagePath })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.success ? result.data : null;
    } catch (error) {
      console.error('Error toggling favorite:', error);
      return null;
    }
  }

  // Get user analytics
  static async getUserAnalytics(): Promise<AnalyticsData | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/page-usage/analytics`, {
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.success ? result.data : null;
    } catch (error) {
      console.error('Error getting user analytics:', error);
      return null;
    }
  }

  // Get quick access recommendations
  static async getQuickAccessRecommendations(limit: number = 8): Promise<PageUsage[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/page-usage/quick-access?limit=${limit}`, {
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.success ? result.data : [];
    } catch (error) {
      console.error('Error getting quick access recommendations:', error);
      return [];
    }
  }

  // Bulk track multiple visits
  static async bulkTrackVisits(visits: PageVisit[]): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/page-usage/track/bulk`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ visits })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.success;
    } catch (error) {
      console.error('Error bulk tracking visits:', error);
      return false;
    }
  }

  // Track page visit with timing
  static trackPageWithTiming(pagePath: string, pageTitle: string, category?: string) {
    const startTime = Date.now();
    
    // Track immediately (without time spent)
    this.trackPageVisit({ pagePath, pageTitle, timeSpent: 0, category });

    // Return a function to call when leaving the page
    return () => {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      this.trackPageVisit({ pagePath, pageTitle, timeSpent, category });
    };
  }

  // Auto-detect category from path
  static detectCategory(pagePath: string): string {
    if (pagePath.includes('/dashboard')) return 'dashboard';
    if (pagePath.includes('/tools/dev-utils')) return 'dev-utils';
    if (pagePath.includes('/tools/project-management')) return 'project-management';
    if (pagePath.includes('/tools')) return 'tools';
    if (pagePath.includes('/settings')) return 'settings';
    return 'other';
  }

  // Get display name for page path
  static getPageDisplayName(pagePath: string): string {
    const pathSegments = pagePath.split('/').filter(Boolean);
    const lastSegment = pathSegments[pathSegments.length - 1] || 'Home';
    
    return lastSegment
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}

export default PageUsageService;
